import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * A seat's settings for a feature, usable only while the seat has the feature.
 *
 * Shown greyed rather than hidden: somebody looking for forwarding on a seat
 * that lacks it should find the controls and the reason, not an absence that
 * reads as a missing screen. The API refuses the write and the node sync
 * withholds the setting as well -- this is the half that says so before
 * anybody tries.
 *
 * One component for every gated tab, reading the same query the Features tab
 * writes, so switching a feature on there updates every tab that depends on it.
 */

export function useEnabledFeatures(tenantId: string, extensionId: string) {
  const q = useQuery({
    queryKey: ['tenants', tenantId, 'extensions', extensionId, 'features'] as const,
    queryFn: () => api.get<{ data: Array<{ key: string; enabled: boolean }> }>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/features`),
  });
  return {
    loading: q.isLoading,
    error: q.error,
    has: (key: string) => (q.data?.data ?? []).some((f) => f.key === key && f.enabled),
  };
}

export function FeatureGate({ tenantId, extensionId, feature, name, scope = 'tenant', children }: {
  tenantId: string;
  extensionId: string;
  /** The feature key, e.g. call_forwarding. */
  feature: string;
  /** What a person calls it. */
  name: string;
  scope?: 'platform' | 'tenant';
  children: ReactNode;
}) {
  const f = useEnabledFeatures(tenantId, extensionId);
  if (f.loading) {
    return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 60 }} /></div>;
  }
  const on = f.has(feature);

  return (
    <>
      {!on && (
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 12 }}>
          <Icon name="info" size={14} />
          <span>
            {name} is not included in this extension's type.{' '}
            {scope === 'platform'
              ? 'Switch it on for this seat under Features to use these settings.'
              : 'Your provider can add it.'}
          </span>
        </div>
      )}
      {/* A disabled fieldset disables every control inside it natively --
          inputs, buttons and selects -- including ones added later. */}
      <fieldset disabled={!on} style={{
        border: 0, margin: 0, padding: 0, minWidth: 0, opacity: on ? 1 : 0.55,
      }}>
        {children}
      </fieldset>
    </>
  );
}
